import React, { ReactNode, useState } from 'react';
import { ProductActionsProvider, ProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { AlgoliaProductHit, AlgoliaProductHitFamilyVariant } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { drawCart, getCartLoading, setCartLoading } from '../utils/legacyCart';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { getStoreCurrency } from '../utils/shopify/getStoreCurrency';
import { AttachAddToCartSlideout } from '../templates/components/attachAddToCartSlideout/AttachAddToCartSlideout';
import {
  AlgoliaHitTransformer,
  getSkuForVariant,
} from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { setMiniCartVisible } from '../templates/components/pdp/utils/utils';
import {
  postAlgoliaAddToCart,
  postAlgoliaProductClick,
  postAlgoliaFilterConvert,
} from '../utils/algolia/search-insights';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { getMarketplacePropertiesBySeller } from '../utils/cart/refreshTieredProductDetails';
import { ShopifyStorefrontClient } from '../api/shopify/ShopifyStorefrontClient';
import { transformStorefrontCartToJbCart } from '../utils/cart/transformStorefrontCartToJbCart';
import { hasReachedMaxQty } from '../utils/checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../utils/checkMaxQty/showMaxQtyAlert';
import { TrackingDescriptorType } from '@jbhi-fi/components/src/Tracking/types';
import { klaviyoAddedToCart } from '../utils/klaviyo';

const storefrontClient = new ShopifyStorefrontClient();

const handleAddToCart = async (
  event: React.SyntheticEvent,
  hit: AlgoliaProductHit,
  variantId?: number,
  trackingDescriptor?: TrackingDescriptorType
) => {
  const variant = hit.children?.find((child: AlgoliaProductHitFamilyVariant) => child.variant_id === variantId);
  const variantID = variant?.variant_id ?? hit.variant_id;
  const handle = variant?.handle ?? hit.handle;
  const shouldUseCustomTracking = Boolean(trackingDescriptor);
  if (hit.button === 'CheckAvailability') {
    window.localStorage.setItem('openAvailabilityModal', 'true');
    window.location.href = `/products/${handle}?variant=${variantID}`;
    return;
  }
  if (hit.button === 'NotifyMe') {
    window.location.href = `/products/${handle}?variant=${variantID}&notifyClicked=true`;
    return;
  }
  if (hit.button === 'View') {
    window.location.href = `/products/${handle}?variant=${variantID}`;
    return;
  }

  if (getCartLoading()) {
    return;
  }
  if (hasReachedMaxQty(variantID)) {
    showMaxQtyAlert();
    return;
  }
  setCartLoading(true);
  const marketplaceSeller = AlgoliaHitTransformer.toMarketplaceSeller(hit);
  const marketplaceProperties = getMarketplacePropertiesBySeller(marketplaceSeller);
  const properties = {
    _availabilityCanPreOrder: hit.availability?.canPreOrder,
    _availabilityFulfilment: hit.availability?.fulfilment,
    _maxQty: hit.availability.fulfilment === 'Digital' ? 1 : hit.product?.limitPerOrder,
    _priceAtc: Math.round(hit.price * 100),
  };
  const attributes = filterAndMapAttributes({ ...properties, ...marketplaceProperties });
  const cart = await storefrontClient.addToCart(variantID, 1, attributes);
  const transformedCart = transformStorefrontCartToJbCart(cart);
  if (shouldUseCustomTracking && trackingDescriptor) {
    const listName = trackingDescriptor?.ecommerceExtraProps?.list ?? trackingDescriptor?.group;
    trackAddToCart({
      location: trackingDescriptor.page,
      currency: getStoreCurrency(),
      addedItems: transformAlgoliaHitForTracking(hit, listName),
    });
  }
  klaviyoAddedToCart(transformedCart);
  postAlgoliaFilterConvert();
  postAlgoliaAddToCart(getSkuForVariant(hit, variantID), hit.__queryID);
  return transformedCart;
};

const handleCardClick = (sku: string, position?: number, queryId?: string) => {
  postAlgoliaProductClick(sku, position, queryId);
};

export interface ShopifyProductActionsProviderProps {
  children: ReactNode;
  attachPageType?: keyof typeof window.Jbhifi.attachConfig.enabledPages;
}

const hideAttachSlideoutSources = ['alternatives'];

export const ShopifyProductActionsProvider = ({ children, attachPageType }: ShopifyProductActionsProviderProps) => {
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [purchasedProduct, setPurchasedProduct] = useState<AlgoliaProductHit | null>(null);
  const isAttachSlideoutEnabled = attachPageType && window.Jbhifi?.attachConfig?.enabledPages?.[attachPageType];
  const hiddenPreambleProductGroupIds = window.Jbhifi?.hiddenProductCardSubtextProductGroupIds
    ?.split(',')
    .map(s => s.trim());

  const onAddToCart: ProductActionsContext['onAddToCart'] = async (
    event,
    hit,
    variantId,
    trackingDescriptor,
    source
  ) => {
    const isMarketplaceProduct = AlgoliaHitTransformer.isMarketplaceProduct(hit);
    const updatedCart = await handleAddToCart(event, hit, variantId, trackingDescriptor);
    if (updatedCart) {
      if (!hideAttachSlideoutSources.includes(source || '') && isAttachSlideoutEnabled && !isMarketplaceProduct) {
        setPurchasedProduct(hit);
        setIsSlideoutOpen(true);
        setMiniCartVisible(false);
      }
      await drawCart(updatedCart);
    }
    return Boolean(updatedCart);
  };

  const handleSlideoutClose = () => {
    setIsSlideoutOpen(false);
    setPurchasedProduct(null);
  };

  return (
    <>
      {purchasedProduct ? (
        <AttachAddToCartSlideout
          isOpen={isSlideoutOpen}
          onClose={handleSlideoutClose}
          pageType={attachPageType === 'collections' ? 'collection' : attachPageType ?? ''}
          purchasedProduct={purchasedProduct}
          freeBonusProducts={[]}
        />
      ) : null}
      <ProductActionsProvider
        onAddToCart={onAddToCart}
        productCartPropsOverrides={{
          shouldHideCartOverlay: isAttachSlideoutEnabled,
          hiddenPreambleProductGroupIds,
          onClick: handleCardClick,
        }}
      >
        {children}
      </ProductActionsProvider>
    </>
  );
};
